import { writable } from 'svelte/store';

import {
	CameraCloudStorageSubscriptionStatus,
	type CameraCloudStorageActivePlan,
	type CameraCloudStorageState,
} from 'src/types/CameraCloudStorage';

const DEFAULT_STATE: CameraCloudStorageState = {
	activePlan: null,
	slotAssignments: {},
};

/**
 * In-memory store for the Camera Cloud Storage feature.
 *
 * The BE owns the truth:
 *   - `user_settings.camera_subscription` → `activePlan`
 *   - `user_settings.camera_list`         → `slotAssignments`
 *
 * `hydrateFromUserSettings` (see `camera_cloud_storage_helpers.ts`)
 * rewrites the store on every `user_settings` change, so account
 * switches naturally discard the previous user's data. We intentionally
 * do *not* persist anything to `localStorage` here — that previously
 * caused per-profile slot assignments to leak across accounts on the
 * same browser.
 */
function create_camera_cloud_storage_store() {
	const { subscribe, set, update } = writable<CameraCloudStorageState>({ ...DEFAULT_STATE });

	return {
		subscribe,
		set,
		setActivePlan: (plan: CameraCloudStorageActivePlan | null) =>
			update((state) => ({ ...state, activePlan: plan })),
		updateActivePlanStatus: (status: CameraCloudStorageSubscriptionStatus) =>
			update((state) => ({
				...state,
				activePlan: state.activePlan ? { ...state.activePlan, status } : state.activePlan,
			})),
		/**
		 * Patch the active plan's `nextBillingTime` (ISO string). The
		 * camera-side `user_settings.camera_subscription` block does not
		 * carry the billing date, so the FE derives it from
		 * `GET /api/subscribe/upgrade-preview/:plantype` and pushes it
		 * into the store via this helper.
		 */
		setNextBillingTime: (nextBillingTime: string | undefined) =>
			update((state) => ({
				...state,
				activePlan: state.activePlan
					? { ...state.activePlan, nextBillingTime }
					: state.activePlan,
			})),
		assignCameraToSlot: (slotIndex: number, cameraId: string | null) =>
			update((state) => {
				const nextAssignments = { ...state.slotAssignments };
				if (cameraId === null) {
					delete nextAssignments[slotIndex];
				} else {
					for (const [key, value] of Object.entries(nextAssignments)) {
						if (value === cameraId && Number(key) !== slotIndex) {
							delete nextAssignments[Number(key)];
						}
					}
					nextAssignments[slotIndex] = cameraId;
				}
				return { ...state, slotAssignments: nextAssignments };
			}),
		setAssignments: (assignments: Record<number, string | null>) =>
			update((state) => ({ ...state, slotAssignments: { ...assignments } })),
		reset: () => set({ ...DEFAULT_STATE }),
	};
}

export const camera_cloud_storage = create_camera_cloud_storage_store();
